/**
 * WebAuthn platform-authenticator assertion over the chain head (FSD §4.2, PWA leg).
 *
 * Two ceremonies, both feature-detected, both OPTIONAL — capture is never blocked by their absence:
 *   1. `registerPlatformCredential()` once per visit (after consent): creates a device-bound key
 *      with `authenticatorAttachment: 'platform'`. The attestation object + clientDataJSON ride
 *      with every signature payload so the be can verify without a separate registration call.
 *   2. `signChainHead(finalChainHash)` per shot: `credentials.get` with the challenge = the raw
 *      32 bytes of h_n. The assertion binds this device's key to the exact chain head.
 *
 * Honest limit, stated on the receipt: the assertion proves the chain head was signed by a key
 * held on THIS device at THIS time. It does not prove the pixels came from a sensor; on a PWA the
 * key is not hardware-attested the way a native keystore key is (that is the native agent leg).
 */

import { hexToBytes } from './chainHash';

export type SignatureUnavailableReason =
  | 'WEBAUTHN_UNSUPPORTED'
  | 'PLATFORM_AUTHENTICATOR_ABSENT'
  | 'REGISTRATION_DECLINED'
  | 'ASSERTION_DECLINED'
  | 'NOT_REGISTERED';

export interface WebAuthnRegistration {
  readonly credentialId: string;
  readonly attestationObject: string;
  readonly clientDataJSON: string;
}

export interface WebAuthnSignaturePayload {
  readonly credentialId: string;
  readonly clientDataJSON: string;
  readonly authenticatorData: string;
  readonly signature: string;
  readonly userHandle: string | null;
  readonly registration: WebAuthnRegistration;
  readonly rpId: string;
}

export type SignResult =
  | { readonly type: 'WEBAUTHN'; readonly payload: WebAuthnSignaturePayload }
  | { readonly unavailable: SignatureUnavailableReason };

export function base64url(bytes: ArrayBuffer | Uint8Array): string {
  const view = ArrayBuffer.isView(bytes) ? (bytes as Uint8Array) : new Uint8Array(bytes as ArrayBuffer);
  let bin = '';
  for (let i = 0; i < view.length; i += 1) bin += String.fromCharCode(view[i] as number);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function rpId(): string {
  return typeof location !== 'undefined' && location.hostname ? location.hostname : 'localhost';
}

export function webauthnSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.create === 'function' &&
    typeof navigator.credentials?.get === 'function'
  );
}

export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!webauthnSupported()) return false;
  try {
    const fn = (window.PublicKeyCredential as unknown as {
      isUserVerifyingPlatformAuthenticatorAvailable?: () => Promise<boolean>;
    }).isUserVerifyingPlatformAuthenticatorAvailable;
    return typeof fn === 'function' ? await fn.call(window.PublicKeyCredential) : false;
  } catch {
    return false;
  }
}

let registration: WebAuthnRegistration | null = null;
let rawCredentialId: Uint8Array | null = null;

export function resetWebAuthnForTests(): void {
  registration = null;
  rawCredentialId = null;
}

/**
 * Register a device-bound platform credential for this visit. Returns the registration or the
 * reason it is unavailable. Never throws.
 */
export async function registerPlatformCredential(
  visitId: string,
  rpName: string,
): Promise<WebAuthnRegistration | { readonly unavailable: SignatureUnavailableReason }> {
  if (registration) return registration;
  if (!webauthnSupported()) return { unavailable: 'WEBAUTHN_UNSUPPORTED' };
  if (!(await isPlatformAuthenticatorAvailable())) return { unavailable: 'PLATFORM_AUTHENTICATOR_ABSENT' };
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const cred = (await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { id: rpId(), name: rpName },
        user: {
          id: new TextEncoder().encode(visitId),
          name: `visit-${visitId}`,
          displayName: rpName,
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 }, // ES256
          { type: 'public-key', alg: -257 }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          residentKey: 'discouraged',
          userVerification: 'preferred',
        },
        timeout: 60_000,
        attestation: 'direct',
      },
    })) as PublicKeyCredential | null;
    if (!cred) return { unavailable: 'REGISTRATION_DECLINED' };
    const att = cred.response as AuthenticatorAttestationResponse;
    rawCredentialId = new Uint8Array(cred.rawId);
    registration = {
      credentialId: base64url(cred.rawId),
      attestationObject: base64url(att.attestationObject),
      clientDataJSON: base64url(att.clientDataJSON),
    };
    return registration;
  } catch {
    return { unavailable: 'REGISTRATION_DECLINED' };
  }
}

/** Sign the chain head with the visit's platform credential. Never throws. */
export async function signChainHead(finalChainHash: string): Promise<SignResult> {
  if (!webauthnSupported()) return { unavailable: 'WEBAUTHN_UNSUPPORTED' };
  if (!registration || !rawCredentialId) return { unavailable: 'NOT_REGISTERED' };
  try {
    const cred = (await navigator.credentials.get({
      publicKey: {
        challenge: hexToBytes(finalChainHash) as BufferSource,
        rpId: rpId(),
        allowCredentials: [{ type: 'public-key', id: rawCredentialId as BufferSource }],
        userVerification: 'preferred',
        timeout: 60_000,
      },
    })) as PublicKeyCredential | null;
    if (!cred) return { unavailable: 'ASSERTION_DECLINED' };
    const a = cred.response as AuthenticatorAssertionResponse;
    return {
      type: 'WEBAUTHN',
      payload: {
        credentialId: base64url(cred.rawId),
        clientDataJSON: base64url(a.clientDataJSON),
        authenticatorData: base64url(a.authenticatorData),
        signature: base64url(a.signature),
        userHandle: a.userHandle ? base64url(a.userHandle) : null,
        registration,
        rpId: rpId(),
      },
    };
  } catch {
    return { unavailable: 'ASSERTION_DECLINED' };
  }
}
